import { z as zod } from 'zod';
import { EmploymentType } from '../networkRequest/officialsService';
import { AgencyOfficialRolesMap } from '../networkRequest/useAgencyData';
import { createDynamicStringValidator, createOptionalPhoneValidator } from './validationTools';
import Utils from './utils';

export interface FormattedFormOfficialData {
  id?: number;
  first_name: string;
  last_name: string;
  email: string | null;
  title: string | null;
  badge_number: string | null;
  rank: string | null;
  phone_number: string | null;
  armed_permission: boolean;
  employment_type: string;
  role_id?: number;
}

export interface FormOfficialData {
  id?: number;
  first_name: string;
  last_name: string;
  title: string;
  email: string;
  badge_number: string;
  role: string;
  rank: string;
  phone_number: string;
  armed_permission: boolean;
  employment_type: EmploymentType;
}

const isSecurityContractor = (formOfficialData: FormOfficialData): boolean => {
  return (formOfficialData.role === 'Contract Security');
};

const validateForm = (
  formOfficialData: FormOfficialData,
  roles: AgencyOfficialRolesMap,
  allowSuperAdmin: boolean,
  setErrors,
  onError
): boolean => {
  const roleNames = Array.from(roles.keys()) as string[];
  // We want our form to be submittable when editing officials who are
  // super admin but it doesn't need to show up elsewhere.
  if (allowSuperAdmin) {
    roleNames.push('Super Admin');
  }
  const zRoleEnum = createDynamicStringValidator(roleNames);

  let schema = zod.object({
    first_name: zod.string().min(1, 'First Name is required.'),
    last_name: zod.string().min(1, 'Last name is required.'),
    role: zRoleEnum
  });
  if (isSecurityContractor(formOfficialData)) {
    schema = schema.extend({
      email: zod.union([zod.literal(null), zod.literal(''), zod.string().email()])
    });
  }
  else {
    schema = schema.extend({
      email: zod.string().email(),
      title: zod.string().optional(),
      badge_number: zod.string().optional(),
      rank: zod.string().optional(),
      phone_number: createOptionalPhoneValidator(),
      type: zod.string().optional(),
      armed_permission: zod.boolean(),
      employment_type: zod.string().min(1)
    });
  }
  return _validateSchema(schema, formOfficialData, setErrors, onError);
};

// moving between what is displayed and what we send to the api
const formatOfficialFormData = (
  formOfficialData: FormOfficialData,
  roles: AgencyOfficialRolesMap
) => {
  const isSuperAdmin = formOfficialData.role === 'Super Admin';
  const matchedRole = _getMatchedRole(roles, formOfficialData);

  if ((matchedRole || isSuperAdmin)) {
    let data: FormattedFormOfficialData;
    if (isSecurityContractor(formOfficialData)) {
      data = {
        first_name: formOfficialData.first_name,
        last_name: formOfficialData.last_name,
        title: null,
        email: Utils.nullIfEmpty(formOfficialData.email),
        badge_number: null,
        rank: null,
        phone_number: null,
        armed_permission: formOfficialData.armed_permission,
        employment_type: 'contractor'
      };
    }
    else {
      data = {
        first_name: formOfficialData.first_name,
        last_name: formOfficialData.last_name,
        title: Utils.nullIfEmpty(formOfficialData.title),
        email: formOfficialData.email,
        badge_number: Utils.nullIfEmpty(formOfficialData.badge_number),
        rank: Utils.nullIfEmpty(formOfficialData.rank),
        phone_number: Utils.nullIfEmpty(formOfficialData.phone_number),
        armed_permission: formOfficialData.armed_permission,
        employment_type: formOfficialData.employment_type
      };
    }
    // Only add the role if it's not super admin (this will prevent us ever
    // sending super admin accidentally or re-setting a super admin to super
    // admin) and also allow us to set other roles.
    if (!isSuperAdmin) data.role_id = matchedRole;

    if (formOfficialData.id) {
      data.id = formOfficialData.id;
    }

    return data;
  }
};

const formatChangedOfficialFormData = (
  formOfficialData: FormOfficialData,
  originalOfficialData: FormOfficialData,
  roles: AgencyOfficialRolesMap
) => {
  const isSuperAdmin = formOfficialData.role === 'Super Admin';
  const matchedRole = _getMatchedRole(roles, formOfficialData);

  if (!(matchedRole || isSuperAdmin)) {
    return null;
  }

  const changedData: Partial<FormattedFormOfficialData> = {};

  const fieldsToCompare: (keyof FormOfficialData)[] = [
    'first_name', 'last_name', 'title', 'email', 'badge_number', 'rank',
    'phone_number', 'armed_permission', 'employment_type'
  ];

  fieldsToCompare.forEach((field) => {
    if (formOfficialData[field] !== originalOfficialData[field]) {
      changedData[field] = formOfficialData[field];
    }
  });

  if (!isSuperAdmin && matchedRole !== _getMatchedRole(roles, originalOfficialData)) {
    changedData.role_id = matchedRole;
  }

  if (Object.keys(changedData).length === 0) {
    return null;
  }

  if (formOfficialData.id) {
    changedData.id = formOfficialData.id;
  }

  return changedData;
};

const _validateSchema = (schema, data, setErrors, onError) => {
  try {
    schema.parse(data);
    setErrors(null);
    return true;
  } catch (formErrors) {
    if (formErrors instanceof zod.ZodError) {
      setErrors(formErrors);
    } else {
      onError('Error creating official.');
    }
    return false;
  }
};

const _getMatchedRole = (roles, official: FormOfficialData) => {
  return roles.get(official.role);
}

export const OfficialFormHelpers = {
  formatChangedOfficialFormData,
  formatOfficialFormData,
  isSecurityContractor,
  validateForm
};
